import { lazy, Suspense, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import { usePageView } from "@/hooks/usePageView";
import { LazyWhenVisible } from "@/hooks/useLazyComponent";

// Lazy load components - they won't be in critical chain because of LazyWhenVisible
const Comparison = lazy(() => import("@/components/Comparison"));
const Features = lazy(() => import("@/components/Features"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const Ecosystem = lazy(() => import("@/components/Ecosystem"));
const CTA = lazy(() => import("@/components/CTA"));
const Footer = lazy(() => import("@/components/Footer"));
const ContactModal = lazy(() => import("@/components/ContactModal"));

const Index = () => {
  const location = useLocation();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const ctaRef = useRef<HTMLDivElement>(null);
  
  // Send PageView event to Facebook with fbp and external_id
  usePageView();

  // Handle hash navigation on page load/refresh
  useEffect(() => {
    if (location.hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }
      }, 100);
    }
  }, [location.hash]);

  const handleContactClick = () => {
    // Scroll to CTA section
    if (ctaRef.current) {
      const headerOffset = 80;
      const elementPosition = ctaRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
    
    // Open modal after a small delay for scroll to start
    setTimeout(() => {
      setContactModalOpen(true);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onContactClick={handleContactClick}
        contactModalOpen={contactModalOpen}
        onContactModalChange={setContactModalOpen}
      />
      <main>
        <Hero onContactClick={handleContactClick} />
        
        {/* Components load only when nearly visible - prevents critical chain loading */}
        <LazyWhenVisible rootMargin="100px" fallback={<div className="h-96" />}>
          <Suspense fallback={<div className="h-96" />}>
            <Comparison />
          </Suspense>
        </LazyWhenVisible>
        
        <LazyWhenVisible rootMargin="150px" fallback={<div className="h-96" />}>
          <Suspense fallback={<div className="h-96" />}>
            <Features />
          </Suspense>
        </LazyWhenVisible>
        
        <LazyWhenVisible rootMargin="150px" fallback={<div className="h-96" />}>
          <Suspense fallback={<div className="h-96" />}>
            <HowItWorks />
          </Suspense>
        </LazyWhenVisible>
        
        <LazyWhenVisible rootMargin="150px" fallback={<div className="h-96" />}>
          <Suspense fallback={<div className="h-96" />}>
            <Ecosystem />
          </Suspense>
        </LazyWhenVisible>
        
        <div ref={ctaRef}>
          <LazyWhenVisible rootMargin="300px" fallback={<div className="h-48" />}>
            <Suspense fallback={<div className="h-48" />}>
              <CTA onContactClick={() => setContactModalOpen(true)} />
            </Suspense>
          </LazyWhenVisible>
        </div>
      </main>
      
      <LazyWhenVisible rootMargin="200px" fallback={<div className="h-24" />}>
        <Suspense fallback={<div className="h-24" />}>
          <Footer />
        </Suspense>
      </LazyWhenVisible>
      
      {contactModalOpen && (
        <Suspense fallback={null}>
          <ContactModal open={contactModalOpen} onOpenChange={setContactModalOpen} />
        </Suspense>
      )}
    </div>
  );
};

export default Index;
